import { ref, computed } from 'vue'
import { defineStore } from 'pinia'
import {
  useLoginMutation,
  useSignUpMutation,
  useVerifyEmailMutation,
  useResetPasswordMutation,
  useForgotPasswordMutation,
  useThirdPartyAuthMutation,
  useExchangeCodeForTokensMutation
} from '@/mutations/auth'
import { useUserInfoQuery } from '@/queries/auth'
import { useAuthModalStore } from './useAuthModalStore'
import { useTokenStore } from './useTokenStore'
import { useUserStore } from './useUserStore'
import type { AuthCredentials, SignUpPayload, ResetPasswordPayload, TokensResponse } from '@/features/auth/api/session'

const isClient = typeof window !== 'undefined'

export const useAuthStore = defineStore('auth', () => {
  const loginMutation = useLoginMutation()
  const signUpMutation = useSignUpMutation()
  const verifyEmailMutation = useVerifyEmailMutation()
  const resetPasswordMutation = useResetPasswordMutation()
  const forgotPasswordMutation = useForgotPasswordMutation()
  const thirdPartyAuthMutation = useThirdPartyAuthMutation()
  const exchangeCodeForTokensMutation = useExchangeCodeForTokensMutation()
  const userInfoQuery = useUserInfoQuery()

  const error = computed(() => {
    return (
      loginMutation.error.value?.message ||
      signUpMutation.error.value?.message ||
      verifyEmailMutation.error.value?.message ||
      resetPasswordMutation.error.value?.message ||
      forgotPasswordMutation.error.value?.message ||
      thirdPartyAuthMutation.error.value?.message ||
      exchangeCodeForTokensMutation.error.value?.message ||
      userInfoQuery.error.value?.message ||
      ''
    )
  })
  const loader = computed(() => {
    return (
      loginMutation.isPending.value ||
      signUpMutation.isPending.value ||
      verifyEmailMutation.isPending.value ||
      resetPasswordMutation.isPending.value ||
      forgotPasswordMutation.isPending.value ||
      thirdPartyAuthMutation.isPending.value ||
      exchangeCodeForTokensMutation.isPending.value ||
      userInfoQuery.isLoading.value
    )
  })
  const currentPopup = ref<Window | null>(null)
  // Set while a third-party popup flow is in flight; cleared by its settle().
  let cancelActivePopupFlow: (() => void) | null = null

  const getUserInfo = async (): Promise<void> => {
    await userInfoQuery.refresh()
    const data = userInfoQuery.data.value
    if (data) {
      const userStore = useUserStore()
      userStore.setUserInfo(data.name, data.email)
      useAuthModalStore().hideAuthModal()
    }
  }

  const auth = async (payload: AuthCredentials) => {
    const data = await loginMutation.mutateAsync(payload)
    if (data) {
      const tokenStore = useTokenStore()
      tokenStore.setTokens(data as unknown as TokensResponse)
    }
    return { data, status: 200 } as { data: unknown; status: number }
  }

  const thirdPartyAuth = async (provider: string) => {
    const data = await thirdPartyAuthMutation.mutateAsync(provider)
    if (data) {
      return openThirdPartyPopup(data.auth_uri, provider)
    }
  }

  const openThirdPartyPopup = (authUri: string, _provider: string) => {
    return new Promise((resolve, reject) => {
      if (!isClient) {
        reject(new Error('Third-party authentication requires a browser environment'))
        return
      }

      const popup = window.open(
        authUri,
        'thirdPartyAuth',
        'width=500,height=600,scrollbars=yes,resizable=yes'
      )

      currentPopup.value = popup

      if (!popup) {
        reject(new Error('Popup blocked by browser'))
        return
      }

      let resultProcessed = false
      let checkClosed = 0
      let timeoutId = 0

      // The ONLY teardown point: every outcome (success, error result, user
      // closing the popup, timeout, external cancel) must release the interval,
      // the timeout and the storage listener, or they leak until the 10-min cap.
      const settle = (finish: () => void) => {
        if (resultProcessed) return
        resultProcessed = true
        window.clearInterval(checkClosed)
        window.clearTimeout(timeoutId)
        window.removeEventListener('storage', handleStorage)
        try {
          if (!popup.closed) {
            popup.close()
          }
        } catch {
          // COOP policy blocked access to the popup
        }
        currentPopup.value = null
        cancelActivePopupFlow = null
        finish()
      }

      const handleAuthenticationResult = (result: Record<string, unknown>) => {
        if (result.type === 'THIRD_PARTY_AUTH_SUCCESS') {
          settle(() => {
            window.localStorage.removeItem('auth_result')
            resolve(result)
          })
        } else if (result.type === 'THIRD_PARTY_AUTH_ERROR') {
          settle(() => {
            window.localStorage.removeItem('auth_result')
            reject(new Error(String(result.error)))
          })
        }
      }

      const handleStorage = (event: StorageEvent) => {
        if (event.key !== 'auth_result' || resultProcessed) {
          return
        }

        try {
          const result = JSON.parse(event.newValue || '{}') as Record<string, unknown>
          handleAuthenticationResult(result)
        } catch (error) {
          console.error('Failed to parse auth result from localStorage:', error)
        }
      }

      window.addEventListener('storage', handleStorage)

      checkClosed = window.setInterval(() => {
        try {
          if (popup.closed) {
            settle(() => reject(new Error('Popup closed by user')))
          }
        } catch {
          // COOP policy blocked access to popup.closed
        }
      }, 1000)

      timeoutId = window.setTimeout(() => {
        settle(() => reject(new Error('Authentication timeout')))
      }, 10 * 60 * 1000)

      cancelActivePopupFlow = () => {
        settle(() => reject(new Error('Third-party auth cancelled')))
      }
    })
  }

  // Lets the auth UI abort an in-flight popup flow on teardown so the
  // 1s poll interval never outlives the component that started it.
  const cancelThirdPartyPopup = () => {
    cancelActivePopupFlow?.()
  }

  const exchangeCodeForTokens = async (code: string) => {
    try {
      const data = await exchangeCodeForTokensMutation.mutateAsync(code)
      if (data) {
        const tokenStore = useTokenStore()
        tokenStore.setTokens(data as unknown as TokensResponse)
      }
    } catch (err: unknown) {
      console.error(err)
      return Promise.reject(err)
    }
  }

  const forgotPassword = async (email: string) => {
    await forgotPasswordMutation.mutateAsync(email)
    const userStore = useUserStore()
    userStore.setTempEmailForPasswordReset(email)
    useAuthModalStore().showAuthModal('checkEmailResetPassword')
  }

  const signUp = async (payload: SignUpPayload) => {
    try {
      const data = await signUpMutation.mutateAsync(payload)
      const userStore = useUserStore()
      userStore.setUserInfo(payload.name, payload.email)
      useAuthModalStore().showAuthModal('verifyUser')
      return { data, status: 200 } as { data: unknown; status: number }
    } catch (err: unknown) {
      console.error(err)
      return err
    }
  }

  const verifyUser = async (token: string) => {
    const data = await verifyEmailMutation.mutateAsync(token)
    if (data) {
      const tokenStore = useTokenStore()
      tokenStore.setTokens(data as unknown as TokensResponse)
    }
  }

  const resetPassword = async (payload: ResetPasswordPayload) => {
    const data = await resetPasswordMutation.mutateAsync(payload)
    if (data) {
      const tokenStore = useTokenStore()
      tokenStore.setTokens(data as unknown as TokensResponse)
    }
  }

  const logout = () => {
    const tokenStore = useTokenStore()
    const userStore = useUserStore()

    tokenStore.clearTokens()
    userStore.clearUserInfo()
  }

  return {
    auth,
    error,
    loader,
    currentPopup,
    logout,
    signUp,
    forgotPassword,
    verifyUser,
    resetPassword,
    thirdPartyAuth,
    cancelThirdPartyPopup,
    exchangeCodeForTokens,
    getUserInfo
  }
})

